import { defineStore } from 'pinia';

export const useDeliveryStore = defineStore('deliveryStore', {
    state: () => ({
        suggestions: [],
        address: '',
        coords: null,
        zone: { id: 1, name: 'Зона 1', coeff: 1.00 },

        // Флаг: адрес/точка вне наших зон
        out_of_zones: false,
        // Служебное: последняя рассчитанная дистанция до центра (км)
        distance_km: null,

        // География зон (центр склада и кольца)
        center: [55.460354, 37.770609], // можешь поменять на свой дефолт
        zones: [
            { id: 1, name: 'Зона 1', from_km:   0, to_km:  10, coeff: 1.00 },
            { id: 2, name: 'Зона 2', from_km:  10, to_km:  25, coeff: 1.15 },
            { id: 3, name: 'Зона 3', from_km:  25, to_km:  45, coeff: 1.30 },
            { id: 4, name: 'Зона 4', from_km:  45, to_km:  70, coeff: 1.50 },
            { id: 5, name: 'Зона 5', from_km:  70, to_km: 100, coeff: 1.75 },
        ],

        // только два режима
        timeMode: 'in_day',         // 'in_day' | 'exact'
        kind: 'truck',              // 'truck' | 'manipulator'
        vehicle: '0_5t',            // truck: 0_5t|1_5t|3_5t|5t|10t; manipulator: 10t

        vehicles: {
            truck: [
                { id: '0_5t', label: '0.5т', maxLenM: 1.5 },
                { id: '1_5t', label: '1.5т', maxLenM: 4 },
                { id: '3_5t', label: '3.5т', maxLenM: 4 },
                { id: '5t',   label: '5т',   maxLenM: 6 },
                { id: '10t',  label: '10т',  maxLenM: 6 },
            ],
            manipulator: [
                { id: '10t', label: '10т', maxLenM: 6, crane: true },
            ]
        },

        // База (в течение дня) для Зоны-1 — точные цифры
        baseZone1InDay: {
            truck: {
                '0_5t': 1379,
                '1_5t': 2199,
                '3_5t': 3429,
                '5t':   6339,
                '10t':  9399,
            },
            manipulator: { '10t': 9399 }
        },

        // Мультипликаторы времени
        timeMultipliers: {
            in_day: 1.000,   // В течение дня
            exact:  2.305    // Точно ко времени
        },
    }),

    getters: {
        // База «в течение дня» для Зоны-1 по выбранному авто
        baseInDayZone1(state) {
            const val = state.baseZone1InDay[state.kind]?.[state.vehicle];
            return typeof val === 'number' ? val : 0;
        },

        // Цена с учётом зоны и выбранного режима (без НДС и прочих доплат)
        rawPrice(state) {
            const inDayZone1 = this.baseInDayZone1;
            const zoneK = state.zone?.coeff ?? 1;
            const timeK = state.timeMultipliers[state.timeMode] ?? 1;
            return inDayZone1 * zoneK * timeK;
        },

        // Маркетинговое округление: вверх до десятка и минус 1
        finalPriceRub() {
            const ceil10 = Math.ceil(this.rawPrice / 10) * 10;
            return Math.max(0, ceil10 - 1);
        },

        finalPriceStr() {
            return new Intl.NumberFormat('ru-RU').format(this.finalPriceRub) + ' ₽';
        },
    },

    actions: {
        // ===== базовые сеттеры =====
        setAddress(address) { this.address = address; },
        setZone(zone) { this.zone = zone || { id: 1, name: 'Зона 1', coeff: 1 }; },
        setKind(kind) {
            this.kind = kind;
            if (kind === 'manipulator') this.vehicle = '10t';
            if (kind === 'truck' && this.vehicle === '10t') this.vehicle = '0_5t';
        },
        setVehicle(v) { this.vehicle = v; },
        setTimeMode(m) { this.timeMode = (m === 'in_day' || m === 'exact') ? m : 'in_day'; },
        setCenter(center) { this.center = Array.isArray(center) ? center : this.center; },
        setZones(zones) { if (Array.isArray(zones) && zones.length) this.zones = zones; },

        // ===== coords + пересчёт зоны и флага =====
        setCoords(coords) {
            this.coords = coords;
            this._updateZoneByCoords(); // ← автоматически ставим out_of_zones и зону
        },

        // ===== служебные функции =====
        _haversineKm([lat1, lon1], [lat2, lon2]) {
            const toRad = x => x * Math.PI / 180;
            const R = 6371;
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a = Math.sin(dLat/2)**2 +
                Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
            return 2 * R * Math.asin(Math.sqrt(a));
        },

        _updateZoneByCoords() {
            if (!Array.isArray(this.coords) || this.coords.length !== 2) return;

            const dist = this._haversineKm(this.center, this.coords);
            this.distance_km = +dist.toFixed(1);

            let found = null;
            for (const z of this.zones) {
                if (dist >= z.from_km && dist < z.to_km) { found = z; break; }
            }

            if (found) {
                this.out_of_zones = false;
                this.setZone(found);
            } else {
                this.out_of_zones = true;
                const last = this.zones[this.zones.length - 1];
                // зона «за пределами» — можно держать отдельным id
                this.setZone({ id: 'beyond', name: 'За зоной', from_km: last.to_km, to_km: Infinity, coeff: last?.coeff ?? 1.75 });
            }
        },
    },
});
